import type { Pack } from './types';
// Parts of Speech / Wortarten is disabled for ALL languages — the "Daily Life" (Alltag) topic
// journey (src/data/pools/dailylife.*.json) replaces it as the grammar-free lesson in each language.
// Particle (JA/KO) and classifier/tone (TH) arcade packs are kept. Re-add an import + BUILT_IN_PACKS
// entry below to restore any Parts of Speech / Wortarten pack.
// import wortartenDe from './wortarten.de.json';
// import wortartenCh from './wortarten.de-CH.json';
// import partsOfSpeechEn from './parts-of-speech.en.json';
// import partsOfSpeechFr from './parts-of-speech.fr.json';
// import partsOfSpeechEs from './parts-of-speech.es.json';
// import partsOfSpeechJa from './parts-of-speech.ja.json';
// import partsOfSpeechKo from './parts-of-speech.ko.json';
// import partsOfSpeechTh from './parts-of-speech.th.json';
import classifiersTh from './classifiers.th.json';
import tonesTh from './tones.th.json';
import particlesJa from './particles.ja.json';
import particlesKo from './particles.ko.json';
import gapsDe from './gaps.de.json';
import gapsCh from './gaps.de-CH.json';
import gapsEn from './gaps.en.json';
import gapsFr from './gaps.fr.json';
import gapsEs from './gaps.es.json';
import gapsIt from './gaps.it.json';
import gapsNl from './gaps.nl.json';
import comprehensionCh from './comprehension.de-CH.json';
import comprehensionDe from './comprehension.de.json';
import comprehensionEn from './comprehension.en.json';
import comprehensionNl from './comprehension.nl.json';
import comprehensionFr from './comprehension.fr.json';
import comprehensionEs from './comprehension.es.json';
import comprehensionIt from './comprehension.it.json';
import comprehensionKo from './comprehension.ko.json';
import comprehensionJa from './comprehension.ja.json';
import comprehensionTh from './comprehension.th.json';
import sentencesCh from './sentences.de-CH.json';
import sentencesDe from './sentences.de.json';
import sentencesEn from './sentences.en.json';
import sentencesFr from './sentences.fr.json';
import sentencesEs from './sentences.es.json';
import sentencesIt from './sentences.it.json';
import sentencesNl from './sentences.nl.json';
import sentencesJa from './sentences.ja.json';
import sentencesKo from './sentences.ko.json';
import sentencesTh from './sentences.th.json';

/** Packs shipped with the app. User-uploaded packs are added at runtime — see game/packs.ts. */
export const BUILT_IN_PACKS: Pack[] = [
  // All Parts of Speech / Wortarten packs are disabled — replaced by the Daily Life (Alltag) topic
  // journey in every language. See the import note above. Particle/classifier/tone packs are kept.
  particlesJa as Pack,
  particlesKo as Pack,
  classifiersTh as Pack,
  tonesTh as Pack,
  // "Was fehlt?" — the gap lesson. Word endings (Kartof___) and cloze sentences (Der Hund ___
  // laut.) are the SAME banner-context mode with different data, so both live in one pack and the
  // sets carry the difficulty ramp: word level first, sentence level after.
  // Every puzzle has exactly ONE answer, which is why the stems are long — compounds and short
  // syllable stems both have several natural completions. See specs/gap-mode.md §5.0.
  // Authored per language, not shared: syllable splits and sentences are the one kind of content
  // that cannot cross languages the way the Picture Words icon set does. ja/ko/th are deliberately
  // absent — the mode needs a written stem that forces one continuation, which their scripts do not
  // give (gaps.test.ts NO_GAP_LANGS).
  gapsDe as Pack,
  gapsCh as Pack,
  gapsEn as Pack,
  gapsFr as Pack,
  gapsEs as Pack,
  gapsIt as Pack,
  gapsNl as Pack,
  // "Bau den Satz" — the word-order lesson. The tiles are the words of one sentence and they have
  // to be smashed IN ORDER into a labelled slot strip; a wrong tap costs a life but leaves the tile
  // standing, because it may hold a word the sentence still needs (specs/sentence-order-mode.md §4).
  // All ten languages. Thai reaches the same safety by a different route: it has no inflection, so
  // its decoys cannot be wrong FORMS — instead every one is a number or a pure classifier, a word
  // class no slot on the board wants. That pool is a closed list the generator enforces, which
  // makes Thai the only language whose decoys are checked mechanically rather than by judgement.
  // See specs/sentence-order-mode.md §13.1.
  sentencesCh as Pack,
  sentencesDe as Pack,
  sentencesEn as Pack,
  sentencesFr as Pack,
  sentencesEs as Pack,
  sentencesIt as Pack,
  sentencesNl as Pack,
  sentencesJa as Pack,
  sentencesKo as Pack,
  sentencesTh as Pack,
  // "Lies und antworte" — the comprehension lesson. Same banner-context engine as the gaps, but
  // the banner is two lines: a question over the sentence that answers it, and the tiles are
  // colours. Every sentence names TWO colours on two different things and carries TWO questions,
  // one per thing — that is what stops "find the colour word" from being a winning strategy and
  // what keeps every tile of a set the right answer somewhere (specs/comprehension-mode.md §4).
  // Seven languages. Unlike the gaps lesson, this content IS portable — the sentences are not tied
  // to language-specific syllable splits (§8.4) — so `de` is DERIVED from the Swiss pack (ß plus
  // the Helvetisms; comprehension.test.ts freezes the derivation) while the rest are authored,
  // because a rival pair has to be re-checked in the target language: the answer tile must occur in
  // its own sentence at a WORD START, and every language breaks that its own way (§11.2, §11.3):
  // Dutch colour sentences are predicative only (attributive changes the stem, geel→gele); French
  // bans "tombe" and "mais", which open with the tiles Tom and mai; Spanish colour sentences are
  // masculine (its plural is additive, blanco→blancos); Italian's are masculine SINGULAR, because
  // the Italian plural changes the final vowel and bianchi does not open with bianco.
  //
  // Korean is the first non-Latin one, and it needed NO change to the word-start anchor: Korean
  // spaces its eojeol and suffixes its particles, so a tile always opens the eojeol it sits in
  // (노란색 opens 노란색이고). It is also the reason two things in the test became per-language —
  // the line budgets, measured against Noto Sans KR rather than guessed, and the question-word
  // patterns, which for a verb-final language cannot be anchored at the start of the question.
  // Japanese follows on the same terms, and the terms are the point: it is written ALL KANA WITH
  // SPACES (わかちがき), the convention sentences.ja.json already uses. That is not a style choice —
  // a natural kanji run has no word boundaries, so the anchor could never fire. Thai stays out for
  // Thai completes the ten. It has no spaces and no convention to adopt, so it is the one language
  // where the rival check drops its word-start anchor and matches a plain substring — looser, but
  // not unsafe, since the check compares SETS and an incidental hit fails loudly. What Thai cannot
  // do is wrap: Phaser breaks lines on spaces, so its line budget is a hard cap (§11.7).
  //
  // LAST on purpose. lessonsForLang() returns the journey pools followed by BUILT_IN_PACKS in
  // registry order, so this array's tail is the tail of the lesson picker — and at 9+ this is the
  // hardest lesson in the game, which belongs at the end of the row rather than in front of the
  // 8+ sentence lessons. Anything appended after it takes that slot away.
  comprehensionCh as Pack,
  comprehensionDe as Pack,
  comprehensionEn as Pack,
  comprehensionNl as Pack,
  comprehensionFr as Pack,
  comprehensionEs as Pack,
  comprehensionIt as Pack,
  comprehensionKo as Pack,
  comprehensionJa as Pack,
  comprehensionTh as Pack,
];

/** Runtime-unique key for a pack (id + lang) — used for selection storage. */
export function packKey(p: Pack): string {
  return `${p.id}__${p.lang}`;
}

/**
 * Validate an arbitrary value matches the Pack schema. Returns the typed Pack or null.
 * Used when loading user-supplied JSON.
 */
export function validatePack(raw: unknown): Pack | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Pack;
  if (typeof p.id !== 'string' || typeof p.title !== 'string' || typeof p.lang !== 'string') return null;
  if (typeof p.version !== 'number') return null;
  if (!Array.isArray(p.classes) || p.classes.length === 0) return null;
  for (const c of p.classes) {
    if (typeof c?.id !== 'string' || typeof c?.label !== 'string' || typeof c?.prompt !== 'string') return null;
  }
  if (!Array.isArray(p.sets) || p.sets.length === 0) return null;
  for (const s of p.sets) {
    if (typeof s?.id !== 'string' || !Array.isArray(s?.words)) return null;
    for (const w of s.words) {
      if (typeof w?.text !== 'string' || typeof w?.class !== 'string') return null;
      if (!p.classes.some((c) => c.id === w.class)) return null;
    }
  }
  return p;
}
